import React from 'react';
import { DownloadIcon, RotateCcwIcon } from 'lucide-react';
import { useAssessment } from '../contexts/AssessmentContext';
import { scenarios } from '../data/ledger';
import { sourceDocuments } from '../data/documents';
import { EVIDENCE_KEY, formatDuration } from '../utils/scoring';
import { downloadRecordPdf } from '../utils/pdf';
import { PackConfidence } from '../types/assessment';
const CONFIDENCE_LABEL: Record<PackConfidence, string> = {
  yes: 'Yes — ready to submit',
  'with-flags': 'With the flags noted',
  no: 'No, material issues remain'
};
export function Admin() {
  const {
    participantId,
    sessionId,
    elapsed,
    promptCount,
    dwell,
    openedDocs,
    results,
    flags,
    events,
    messages,
    reflection,
    packConfidence,
    submitted,
    startedAt,
    resetSession
  } = useAssessment();
  const totalDwell = dwell.reconciliation + dwell.budget + dwell.documents;
  const sourceShare = totalDwell === 0 ? 0 : Math.round(dwell.documents / totalDwell * 100);
  const metrics = [{
    label: 'Prompts to assistant',
    value: promptCount.toString(),
    sub: `${messages.length} messages total`
  }, {
    label: 'Documents opened',
    value: `${openedDocs.length} / ${sourceDocuments.length}`,
    sub: openedDocs.join(', ') || 'none'
  }, {
    label: 'Time in source documents',
    value: formatDuration(dwell.documents),
    sub: `${sourceShare}% of workspace time`
  }, {
    label: 'Elapsed',
    value: formatDuration(elapsed),
    sub: submitted ? 'Submitted' : startedAt ? 'In progress' : 'Not started'
  }];
  const dwellBars = [{
    label: 'Source documents',
    seconds: dwell.documents,
    color: 'bg-teal-500'
  }, {
    label: 'Reconciliation',
    seconds: dwell.reconciliation,
    color: 'bg-slate-700'
  }, {
    label: 'P&L vs Budget',
    seconds: dwell.budget,
    color: 'bg-slate-400'
  }];
  return <main className="w-full flex-1 bg-slate-100 pb-16" data-id="element-331" data-mp="2dzmp61i4dgxv">
      <div className="border-b border-slate-800 bg-slate-900" data-id="element-332" data-mp="2e04v5pul39iz">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-end justify-between gap-4 px-6 py-8" data-id="element-333" data-mp="24aplv5vjpc1h">
          <div data-id="element-334" data-mp="29bvihesbtjga">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-400" data-id="element-335" data-mp="29cexonqldtqm">
              Reviewer view · not shown to the participant
            </p>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-white" data-id="element-336" data-mp="1zbuuvgnfch6a">
              Session record — {participantId || 'no participant'}{' '}
              <span className="font-mono-num text-base font-normal text-slate-400" data-id="element-337" data-mp="2n4l2xes7igba">{sessionId}</span>
            </h1>
            <p className="mt-1 text-sm text-slate-400" data-id="element-338" data-mp="2dcp39zah2i4y">
              A record of what happened. The categories below are descriptions, not scores.
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-339" data-mp="2i95retho8wi1">
            <button type="button" onClick={resetSession} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-3 text-sm font-medium text-slate-300 transition-colors hover:border-slate-600 hover:text-white" data-id="element-340" data-mp="3601pu9gmupy9">
              <RotateCcwIcon className="h-4 w-4" data-id="element-341" data-mp="ucpv37zvgtwb" />
              Reset session
            </button>
            <button type="button" onClick={() => downloadRecordPdf({
            participantId,
            sessionId,
            elapsedSeconds: elapsed,
            promptCount,
            dwell,
            openedDocs,
            results,
            flags,
            reflection,
            packConfidence,
            messages
          })} className="inline-flex items-center gap-2 rounded-lg bg-teal-400 px-5 py-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-teal-300" data-id="element-342" data-mp="191lzmv2pzos6">
              <DownloadIcon className="h-4 w-4" data-id="element-343" data-mp="1ws89mrwabj4t" />
              Download 2-page record
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6" data-id="element-344" data-mp="2s3pwayz9wqze">
        <div className="-mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-345" data-mp="2w402ocqz9jes">
          {metrics.map(m => <div key={m.label} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-346" data-mp="313clmmeqa6c3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-347" data-mp="361w7kmtbni4g">
                {m.label}
              </p>
              <p className="mt-2 font-mono-num text-2xl font-semibold text-slate-900" data-id="element-348" data-mp="3b0h8kogee5ty">{m.value}</p>
              <p className="mt-1 truncate text-xs text-slate-500" title={m.sub} data-id="element-349" data-mp="3f1jaxe5m2y05">
                {m.sub}
              </p>
            </div>)}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]" data-id="element-350" data-mp="3ot6y32v4xsvt">
          <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-351" data-mp="3ts0f359ms20q">
            <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4" data-id="element-352" data-mp="2lgtqw664to6">
              <h2 className="text-sm font-semibold text-slate-900" data-id="element-353" data-mp="1n3htjk14jat5">Scenario record</h2>
              <span className="text-xs text-slate-500" data-id="element-354" data-mp="1n157auoc2eaa">
                Whole-pack sign-off:{' '}
                <span className="font-medium text-slate-800" data-id="element-355" data-mp="1i6rgz6oj2xch">
                  {packConfidence ? CONFIDENCE_LABEL[packConfidence] : '—'}
                </span>
              </span>
            </header>
            <ul className="divide-y divide-slate-100" data-id="element-356" data-mp="188ldwlqzjl61">
              {results.map(r => {
              const spec = scenarios.find(s => s.id === r.scenarioId);
              if (!spec) return null;
              const key = EVIDENCE_KEY[r.category];
              return <li key={r.scenarioId} className="px-5 py-4" data-id="element-357" data-mp="1w23ajaqy6hrt">
                    <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-358" data-mp="1r4bjsk21h1p7">
                      <div className="max-w-xl" data-id="element-359" data-mp="2olio3s48veey">
                        <p className="font-mono-num text-[11px] uppercase tracking-wider text-slate-400" data-id="element-360" data-mp="2ombng80vj1n2">
                          {r.scenarioId} · {spec.klass} · {spec.rowId}
                        </p>
                        <p className="mt-1 text-sm text-slate-900" data-id="element-361" data-mp="hykstkb0rjzf">{spec.vehicle}</p>
                      </div>
                      <span className={`shrink-0 rounded-full border px-3 py-1 text-[11.5px] font-semibold ${key.className}`} data-id="element-362" data-mp="86oqfx1wh7pe">
                        {key.label}
                      </span>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-slate-600" data-id="element-363" data-mp="3v9tiwewbs573">{r.evidence}</p>
                    <p className="mt-1.5 text-[11px] text-slate-400" data-id="element-364" data-mp="38n7rd8dzaeg">
                      Full credit: {spec.fullCredit} · decisive source {spec.decisiveSourceId}
                    </p>
                  </li>;
            })}
            </ul>
          </section>

          <div className="space-y-6" data-id="element-365" data-mp="1jmvk6ezg03cd">
            <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-366" data-mp="1jjr86vfqk3b5">
              <h2 className="text-sm font-semibold text-slate-900" data-id="element-367" data-mp="1oikk4jfdjm97">Where time went</h2>
              <div className="mt-4 space-y-4" data-id="element-368" data-mp="392z4unbapz80">
                {dwellBars.map(b => {
                const pct = totalDwell === 0 ? 0 : b.seconds / totalDwell * 100;
                return <div key={b.label} data-id="element-369" data-mp="3ittwk77b9r0u">
                      <div className="flex items-baseline justify-between" data-id="element-370" data-mp="3e192d4khtbws">
                        <p className="text-xs font-medium text-slate-700" data-id="element-371" data-mp="3dzy23eq9amaf">{b.label}</p>
                        <p className="font-mono-num text-xs text-slate-500" data-id="element-372" data-mp="3rva4876qrf1d">
                          {formatDuration(b.seconds)} · {Math.round(pct)}%
                        </p>
                      </div>
                      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100" data-id="element-373" data-mp="5jmnrytm6ojc">
                        <div className={`h-full rounded-full transition-all duration-500 ${b.color}`} style={{
                      width: `${pct}%`
                    }} data-id="element-374" data-mp="l1qa75qyiyl" />
                      </div>
                    </div>;
              })}
              </div>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-375" data-mp="1ljemgsf1iarn">
              <h2 className="text-sm font-semibold text-slate-900" data-id="element-376" data-mp="1qad9eauja73v">Entries</h2>
              {Object.keys(flags).length === 0 ? <p className="mt-3 text-sm text-slate-400" data-id="element-377" data-mp="1v9gd9rys27a9">No flags or corrections recorded.</p> : <ul className="mt-3 space-y-3" data-id="element-378" data-mp="xo29l2er4a84">
                  {Object.values(flags).map(f => <li key={f.rowId} className="rounded-lg border border-slate-200 p-3" data-id="element-379" data-mp="12neu3gi5h57j">
                      <p className="font-mono-num text-[11px] text-slate-500" data-id="element-380" data-mp="17j3wdrlk107j">
                        {f.rowId} · {f.kind} · {f.actionType} · sign-off {f.signOff}
                      </p>
                      <p className="mt-1 text-[13px] text-slate-800" data-id="element-381" data-mp="2iz0uo6fa8whf">{f.what}</p>
                      <p className="mt-1 text-[11px] text-slate-500" data-id="element-382" data-mp="2n048kndrsig0">
                        Sources: {f.sources.join(', ') || 'none cited'}
                      </p>
                    </li>)}
                </ul>}
            </section>
          </div>
        </div>

        <section className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-383" data-mp="2kgsgegr5p4y">
          <header className="border-b border-slate-200 px-5 py-4" data-id="element-384" data-mp="3tsl2vxnrfo7z">
            <h2 className="text-sm font-semibold text-slate-900" data-id="element-385" data-mp="3osp1m66g6aw4">Event log</h2>
            <p className="mt-0.5 text-xs text-slate-500" data-id="element-386" data-mp="3oqm1gtbkz1do">{events.length} events captured</p>
          </header>
          <div className="max-h-96 overflow-y-auto" data-id="element-387" data-mp="3jvyj7c76737j">
            {events.length === 0 ? <p className="px-5 py-8 text-center text-sm text-slate-400" data-id="element-388" data-mp="3ftv84ooey0ms">No activity recorded yet.</p> : <table className="w-full text-sm" data-id="element-389" data-mp="1e0dpyr2nphds">
                <tbody data-id="element-390" data-mp="1e05f14q3gm58">
                  {[...events].reverse().map(e => <tr key={e.id} className="border-b border-slate-100 last:border-0" data-id="element-391" data-mp="192m1y854ufev">
                      <td className="w-24 whitespace-nowrap px-5 py-2 font-mono-num text-xs text-slate-400" data-id="element-392" data-mp="2gpnhqjlgymdt">
                        {new Date(e.at).toLocaleTimeString()}
                      </td>
                      <td className="w-28 px-2 py-2" data-id="element-393" data-mp="2prd9jqj9jnc4">
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600" data-id="element-394" data-mp="2por2rt475buy">
                          {e.type}
                        </span>
                      </td>
                      <td className="px-5 py-2 text-slate-700" data-id="element-395" data-mp="1t1dz1qjjyeqw">{e.detail}</td>
                    </tr>)}
                </tbody>
              </table>}
          </div>
        </section>
      </div>
    </main>;
}